import { Spin } from 'antd'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { PageLayout } from '../components/PageLayout/PageLayout'
import { PropertyDetailFetchError } from '../components/PropertyDetailFetchError/PropertyDetailFetchError'
import { SubjectSearchBar } from '../components/SubjectSearchBar/SubjectSearchBar'
import { SubjectSearchResult } from '../components/SubjectSearchResult/SubjectSearchResult'
import { useFetchSubjectSearch } from '../utils/useFetchSubjectSearch'

type SearchParams = { name: string; address: string; history?: boolean }

export const Subject: FunctionComponent = () => {
  const location = useLocation()
  const history = useHistory()

  const prevValues = useRef({ name: '', address: '' })
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const { fetching, error, data, refetch } = useFetchSubjectSearch()

  const setActiveKey = useCallback(
    (values: SearchParams) => {
      // TODO: remove this line when the new auth is deployed
      queryParams.delete('t')
      queryParams.set('name', values.name)
      if (values.address) queryParams.set('address', values.address)

      history.push({
        pathname: location.pathname,
        search: queryParams.toString(),
      })
    },
    [history, location.pathname, queryParams]
  )

  const handleSearch = useCallback(
    (values: SearchParams) => {
      prevValues.current = values
      refetch(values.name, values.address, values.history)
      setActiveKey(values)
    },
    [refetch, setActiveKey]
  )

  useEffect(() => {
    const name = queryParams.get('name') || ''
    const address = queryParams.get('address') || ''

    if (
      !name ||
      (name === prevValues.current.name &&
        address === prevValues.current.address)
    )
      return

    handleSearch({ name, address })
  }, [handleSearch, queryParams])

  const contentType = useMemo(() => {
    switch (true) {
      case fetching:
        return 'fetching'
      case !prevValues.current.name:
        return 'initial'
      case error:
        return 'error'
      default:
        return 'found'
    }
  }, [fetching, error])

  return (
    <>
      <PageLayout title="Vyhledat subjekt" className="!py-0 mb-6">
        <SubjectSearchBar loading={fetching} onSubmit={handleSearch} />
      </PageLayout>

      {contentType === 'error' && (
        <PropertyDetailFetchError
          message={`Vyhledávání subjektu ${prevValues.current.name} selhalo.`}
          description="Je nám líto, ale subjekt se nepodařilo vyhledat. Opakujte prosím akci později, nebo se obraťte na technickou podporu."
        />
      )}

      {(contentType === 'found' || contentType === 'fetching') && (
        <Spin spinning={contentType === 'fetching'}>
          <SubjectSearchResult data={data} />
        </Spin>
      )}
    </>
  )
}

export default Subject
